import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@nesso/ui/primitives/button';

import {
    EMAIL_IN_USE_ERROR_TYPE,
    formatEmailInUseMessage,
    type GetClientProfileOutput,
    resolveEmailInUseError,
    type UpdateClientProfileInput,
    useUpdateClientProfileOptions,
} from '@/entities/client';
import { useSaveThenGoBack } from '@/shared/lib/use-save-then-go-back';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { ContactPreferencesSection } from '@/shared/ui/forms/contact-preferences-section';
import { SheetFooter } from '@/shared/ui/panels/sheet-panel';

import { buildEditClientProfileDefaults } from '../../lib/build-edit-client-profile-defaults';
import { editClientProfileFormSchema } from '../../model/edit-client-profile.schema';
import { EditClientProfileFormAddress } from './edit-client-profile-form/edit-client-profile-form-address';
import { EditClientProfileFormContact } from './edit-client-profile-form/edit-client-profile-form-contact';
import { EditClientProfileFormDemographics } from './edit-client-profile-form/edit-client-profile-form-demographics';
import { EditClientProfileFormPersonalDetails } from './edit-client-profile-form/edit-client-profile-form-personal-details';

type Props = {
    profile: GetClientProfileOutput;
    onDirtyChange: (isDirty: boolean) => void;
};

export function EditClientProfileForm({ profile, onDirtyChange }: Props) {
    const goBack = useSheetStore((state) => state.goBack);

    const updateProfileMutation = useMutation(useUpdateClientProfileOptions(profile.id));

    const form = useForm<UpdateClientProfileInput>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: zodResolver(editClientProfileFormSchema, undefined, { raw: true }),
        defaultValues: buildEditClientProfileDefaults(profile),
    });

    const { isDirty, isValid } = form.formState;

    // The cleanup disarms the shell's discard guard when this form unmounts (the client changed or
    // the sheet closed), so a form that is gone cannot leave it armed for the next one.
    useEffect(() => {
        onDirtyChange(isDirty);
        return () => onDirtyChange(false);
    }, [isDirty, onDirtyChange]);

    // Any other failure surfaces through the mutation's own toast.
    const saveThenGoBack = useSaveThenGoBack((error) => {
        const emailInUse = resolveEmailInUseError(error);
        if (emailInUse) {
            form.setError('emailPrimary', {
                type: EMAIL_IN_USE_ERROR_TYPE,
                message: formatEmailInUseMessage(emailInUse.holder).message,
            });
        }
    });

    const onSubmit = (data: UpdateClientProfileInput) =>
        saveThenGoBack(() => updateProfileMutation.mutateAsync(data));

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
                <div className="flex-1 overflow-y-auto">
                    <EditClientProfileFormPersonalDetails />
                    <EditClientProfileFormContact />
                    <EditClientProfileFormAddress />
                    <ContactPreferencesSection />
                    <EditClientProfileFormDemographics />
                </div>
                <SheetFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={updateProfileMutation.isPending}
                        onClick={() => goBack()}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!isDirty || !isValid || updateProfileMutation.isPending}
                    >
                        Save
                    </Button>
                </SheetFooter>
            </form>
        </FormProvider>
    );
}
